import React, { useMemo, useState } from "react";

/**
 * props:
 *  - data: Array<Object>
 *  - columns: Array<{
 *      key: string,
 *      header: string,
 *      type?: 'string'|'number'|'date',
 *      sortable?: boolean,                   // 기본 true (false면 정렬 비활성)
 *      sortBy?: (row: any) => any,           // 정렬용 파생값(옵션)
 *      render?: (value: any, row: any, rowIndex: number) => React.ReactNode,
 *      headerRender?: () => React.ReactNode // 헤더 커스텀(예: 전체선택 체크박스)
 *  }>
 *  - defaultSort?: { key: string, dir: 'asc'|'desc' }
 *  - rowKey?: string
 *  - onRowClick?: (row) => void
 *  - manualSorting?: boolean                // 서버에서 정렬한 순서 유지 (헤더 동작은 유지)
 *  - rowClassName?: (row) => string
 *  - tableClassName?: string
 *  - headClassName?: string
 *  - bodyClassName?: string
 *  - columnWidths?: string[]                // 예: ['5rem','10rem','auto']
 */
const SortableTable = ({
    data = [],
    columns = [],
    defaultSort,
    sort: controlledSort,
    onSortChange,
    rowKey,
    onRowClick,
    rowClassName,
    tableClassName = "table-fixed border-collapse",
    headClassName = "",
    bodyClassName = "",
    columnWidths,
    headerRowClassName = "",
    headerCellClassName = "",
    bodyCellClassName = "whitespace-nowrap",  // 본문 전체 1줄 고정
    striped = false,
    manualSorting = false,
}) => {
    const [sort, setSort] = useState(() => {
        if (defaultSort && defaultSort.key && defaultSort.dir) {
            return defaultSort; // { key, dir: 'asc'|'desc' }
        }
        return { key: null, dir: "asc" };
    });
    const activeSort = controlledSort ?? sort;

    const totalPx = useMemo(() => {
        if (!Array.isArray(columnWidths) || columnWidths.length === 0) return null;
        // 모든 값이 'Npx' 인 경우에만 합산
        const nums = columnWidths.map((w) => {
            const n = parseInt(String(w), 10);
            return Number.isFinite(n) ? n : NaN;
        });
        return nums.every((n) => Number.isFinite(n))
            ? nums.reduce((a, b) => a + b, 0)
            : null;
    }, [columnWidths]);

    // 오름/내림 2-단계 토글
    const handleHeaderClick = (key, isSortable = true) => {
        if (!isSortable) return;
        const nextSort =
            activeSort.key !== key
                ? { key, dir: "asc" }
                : { key, dir: activeSort.dir === "asc" ? "desc" : "asc" };

        if (controlledSort === undefined) {
            setSort(nextSort);
        }
        onSortChange?.(nextSort);
    };

    const getType = (col, sampleValue) => {
        if (col.type) return col.type;
        if (typeof sampleValue === "number") return "number";
        if (sampleValue instanceof Date) return "date";
        if (typeof sampleValue === "string" && !Number.isNaN(Date.parse(sampleValue))) return "date";
        if (typeof sampleValue === "string") return "string";
        return "string";
    };

    const normalize = (val, type) => {
        if (val === null || val === undefined) {
            if (type === "number" || type === "date") return Number.NEGATIVE_INFINITY;
            return "";
        }
        if (type === "number") {
            const num = typeof val === "number" ? val : Number(val);
            return Number.isFinite(num) ? num : Number.NEGATIVE_INFINITY;
        }
        if (type === "date") {
            const ts = val instanceof Date ? val.getTime() : Date.parse(val);
            return Number.isFinite(ts) ? ts : Number.NEGATIVE_INFINITY;
        }
        return String(val);
    };

    const sorted = useMemo(() => {
        if (manualSorting) return data;
        if (!activeSort.key) return data;
        const col = columns.find((c) => c.key === activeSort.key);
        // if (!col) return data;
        if (activeSort.key === "_rownum") {
            return activeSort.dir === "asc" ? data : [...data].reverse();
        }

        const sample = data.find((row) => {
            const val = col.sortBy ? col.sortBy(row) : row[activeSort.key];
            return val !== undefined;
        })?.[activeSort.key];
        // sample 은 type 추론용이라 아래서 실제 값 추출 시 sortBy 우선 적용
        const type = getType(col, sample);

        return data
            .map((row, idx) => ({ row, idx }))
            .sort((a, b) => {
                const aVal = col.sortBy ? col.sortBy(a.row) : a.row[activeSort.key];
                const bVal = col.sortBy ? col.sortBy(b.row) : b.row[activeSort.key];
                const av = normalize(aVal, type);
                const bv = normalize(bVal, type);

                let cmp = 0;
                if (type === "string") {
                    cmp = String(av).localeCompare(String(bv), undefined, {
                        sensitivity: "base",
                        numeric: true,
                    });
                } else {
                    cmp = av < bv ? -1 : av > bv ? 1 : 0;
                }
                if (cmp === 0) cmp = a.idx - b.idx; // 안정 정렬
                return activeSort.dir === "asc" ? cmp : -cmp;
            })
            .map((x) => x.row);
    }, [data, columns, activeSort, manualSorting]);

    // 비활성(정렬 가능) ▼ / 활성 ▲▼
    const icon = (key, isSortable) => {
        if (!isSortable) return "";
        if (activeSort.key !== key) return "▼";          // 중립 아이콘(정렬 가능 표시)
        return activeSort.dir === "asc" ? "▲" : "▼";     // 활성 아이콘
    };

    return (
        <div className="w-full overflow-x-auto">
            <table className={tableClassName} style={totalPx ? { width: totalPx } : undefined}>
                {Array.isArray(columnWidths) && columnWidths.length > 0 && (
                    <colgroup>
                        {columnWidths.map((w, i) => (
                            <col key={i} style={{ width: w }} />
                        ))}
                    </colgroup>
                )}
                <thead className={headClassName}>
                    <tr className={headerRowClassName}>
                        {columns.map((col) => {
                            const isSortable = col.sortable !== false;
                            const hasHeaderRender = typeof col.headerRender === "function";
                            const headerIsElement = React.isValidElement(col.header);
                            const clickable = isSortable && !hasHeaderRender;
                            const isActive = clickable && activeSort.key === col.key;
                            const ico = icon(col.key, isSortable);

                            const headerAlign =
                                col.align === "center"
                                    ? "text-center"
                                    : col.align === "right"
                                        ? "text-right"
                                        : "text-left";

                            const thClass = [
                                "px-2 py-2 border-b select-none whitespace-nowrap",
                                clickable ? "cursor-pointer" : "cursor-default",
                                headerCellClassName,
                                col.headerClassName || headerAlign,
                            ].join(" ");

                            // ✅ 아이콘 색상만 정렬 상태에 따라 변경
                            const iconColorClass = !isSortable
                                ? "text-transparent"              // 정렬 불가 컬럼 → 아이콘 숨김
                                : isActive
                                    ? "text-[#7174FF]"               // 정렬 중인 컬럼 → 보라색
                                    : "text-neutral-300";            // 나머지 → 연한 회색

                            return (
                                <th
                                    key={col.key}
                                    onClick={
                                        clickable ? () => handleHeaderClick(col.key, isSortable) : undefined
                                    }
                                    onKeyDown={
                                        clickable
                                            ? (e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    handleHeaderClick(col.key, isSortable);
                                                }
                                            }
                                            : undefined
                                    }
                                    tabIndex={clickable ? 0 : -1}
                                    aria-sort={
                                        isActive
                                            ? activeSort.dir === "asc"
                                                ? "ascending"
                                                : "descending"
                                            : "none"
                                    }
                                    className={thClass}
                                    title={clickable ? "정렬 토글 (오름/내림)" : undefined}
                                >
                                    {hasHeaderRender ? (
                                        col.headerRender()
                                    ) : headerIsElement ? (
                                        col.header
                                    ) : (
                                        <span className="inline-flex items-center gap-2 max-w-full">
                                            {/* 🔹 헤더 텍스트는 기존 색 유지 (별도 class 안 줌) */}
                                            <span className="truncate">{col.header}</span>

                                            {/* 🔹 화살표 아이콘만 상태에 따라 색상 변경 */}
                                            <span
                                                className={
                                                    "inline-block w-4 text-center " + iconColorClass
                                                }
                                                aria-hidden="true"
                                            >
                                                {ico}
                                            </span>
                                        </span>
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                </thead>


                <tbody className={bodyClassName}>
                    {sorted.map((row, i) => {
                        const k = rowKey ? (row[rowKey] ?? i) : i;
                        const cls = [
                            rowClassName ? rowClassName(row) : "",
                            onRowClick ? "hover:bg-gray-50 cursor-pointer" : "",
                            striped ? "odd:bg-gray-50" : "",
                        ].join(" ");
                        return (
                            <tr
                                key={k}
                                className={cls}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                tabIndex={onRowClick ? 0 : -1}
                                role={onRowClick ? "button" : undefined}
                                onKeyDown={
                                    onRowClick
                                        ? (e) => {
                                            if (e.key === "Enter" || e.key === " ") onRowClick(row);
                                        }
                                        : undefined
                                }
                            >
                                {columns.map((col) => {
                                    const raw = row[col.key];
                                    const cellAlign = col.align === "center" ? "text-center"
                                        : col.align === "right" ? "text-right"
                                            : "text-left";
                                    const tdClass = ["px-2 py-2 border-b", bodyCellClassName, col.cellClassName || cellAlign].join(" ");
                                    return (
                                        <td key={col.key} className={tdClass}>
                                            {col.key === "_rownum"
                                                ? (activeSort.dir === "desc" ? (sorted.length - i) : (i + 1))
                                                : (col.render ? col.render(raw, row, i) : String(raw ?? ""))}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    {sorted.length === 0 && (
                        <tr>
                            <td className="px-3 py-6 text-center text-gray-500" colSpan={columns.length}>
                                데이터가 없습니다.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default SortableTable;
